import React, { useEffect, useState } from 'react';
import api from '../api';
import { 
  ShoppingCart, Users, Clock, CarSimple, Wrench, CalendarCheck,
  Warning, CheckCircle, CurrencyNgn, TrendUp, ArrowRight,
  ClipboardText, FileText, UserPlus, Package
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await api.get('/dashboard/overview');
      setOverview(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', { 
      style: 'currency', 
      currency: 'NGN',
      minimumFractionDigits: 0 
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B2560D]"></div>
      </div>
    );
  }

  const modules = overview?.modules || {};

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#2B3210] mb-2">
          Dashboard
        </h1>
        <p className="text-base text-[#505631]">
          Welcome to Abule Imole Administration Portal
          {user?.first_name && `, ${user.first_name}`}
        </p>
      </div>

      {/* Module Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        
        {/* Procurement Module */}
        {modules.procurement && (
          <Link to="/procurement" className="block">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6 card-hover h-full" data-testid="procurement-card">
              <div className="flex items-start justify-between mb-4">
                <div className="bg-orange-50 text-[#B2560D] p-3 rounded-lg">
                  <ShoppingCart size={24} weight="duotone" />
                </div>
                <ArrowRight size={20} className="text-[#505631]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2B3210] mb-3">Procurement</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#505631]">Pending Requests</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    modules.procurement.pending_requests > 0 
                      ? 'bg-amber-100 text-amber-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {modules.procurement.pending_requests}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#505631]">Approved</span>
                  <span className="text-sm font-medium text-[#2B3210]">{modules.procurement.approved_requests}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-[#E5E2D9]">
                  <span className="text-sm text-[#505631]">Total Spent</span>
                  <span className="text-sm font-bold text-[#B2560D]">{formatCurrency(modules.procurement.total_spent)}</span>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Timesheets Module */}
        {modules.timesheets && (
          <Link to="/timesheets" className="block">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6 card-hover h-full" data-testid="timesheets-card">
              <div className="flex items-start justify-between mb-4">
                <div className="bg-blue-50 text-blue-600 p-3 rounded-lg">
                  <Clock size={24} weight="duotone" />
                </div>
                <ArrowRight size={20} className="text-[#505631]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2B3210] mb-3">Timesheets</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#505631]">Pending Approval</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    modules.timesheets.pending_timesheets > 0 
                      ? 'bg-amber-100 text-amber-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {modules.timesheets.pending_timesheets}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#505631]">Total Timesheets</span>
                  <span className="text-sm font-medium text-[#2B3210]">{modules.timesheets.total_timesheets}</span>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Onboarding Module */}
        {modules.onboarding && (
          <Link to="/onboarding" className="block">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6 card-hover h-full" data-testid="onboarding-card">
              <div className="flex items-start justify-between mb-4">
                <div className="bg-emerald-50 text-emerald-600 p-3 rounded-lg">
                  <UserPlus size={24} weight="duotone" />
                </div>
                <ArrowRight size={20} className="text-[#505631]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2B3210] mb-3">Onboarding</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#505631]">In Onboarding</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    modules.onboarding.onboarding_employees > 0 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {modules.onboarding.onboarding_employees}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#505631]">Active Employees</span>
                  <span className="text-sm font-medium text-[#2B3210]">{modules.onboarding.active_employees}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#505631]">Pending Contracts</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    modules.onboarding.pending_contracts > 0 
                      ? 'bg-amber-100 text-amber-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {modules.onboarding.pending_contracts}
                  </span>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Transportation Module */}
        {modules.transportation && (
          <Link to="/transportation" className="block">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6 card-hover h-full" data-testid="transportation-card">
              <div className="flex items-start justify-between mb-4">
                <div className="bg-purple-50 text-purple-600 p-3 rounded-lg">
                  <CarSimple size={24} weight="duotone" />
                </div>
                <ArrowRight size={20} className="text-[#505631]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2B3210] mb-3">Transportation</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#505631]">Pending Bookings</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    modules.transportation.pending_bookings > 0 
                      ? 'bg-amber-100 text-amber-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {modules.transportation.pending_bookings}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#505631]">Today's Bookings</span>
                  <span className="text-sm font-medium text-[#2B3210]">{modules.transportation.today_bookings}</span>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Facilities Module */}
        {modules.facilities && (
          <Link to="/facilities" className="block">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6 card-hover h-full" data-testid="facilities-card">
              <div className="flex items-start justify-between mb-4">
                <div className="bg-cyan-50 text-cyan-600 p-3 rounded-lg">
                  <Wrench size={24} weight="duotone" />
                </div>
                <ArrowRight size={20} className="text-[#505631]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2B3210] mb-3">Facilities</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#505631]">Open Work Orders</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    modules.facilities.open_work_orders > 0 
                      ? 'bg-amber-100 text-amber-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {modules.facilities.open_work_orders}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#505631]">Critical Issues</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    modules.facilities.critical_work_orders > 0 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {modules.facilities.critical_work_orders}
                  </span>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Events Module */}
        {modules.events && (
          <Link to="/events" className="block">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6 card-hover h-full" data-testid="events-card">
              <div className="flex items-start justify-between mb-4">
                <div className="bg-rose-50 text-rose-600 p-3 rounded-lg">
                  <CalendarCheck size={24} weight="duotone" />
                </div>
                <ArrowRight size={20} className="text-[#505631]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2B3210] mb-3">Events</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#505631]">Active Events</span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    {modules.events.active_events}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#505631]">Pending Invoices</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    modules.events.pending_invoices > 0 
                      ? 'bg-amber-100 text-amber-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {modules.events.pending_invoices}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-[#E5E2D9]">
                  <span className="text-sm text-[#505631]">Outstanding</span>
                  <span className={`text-sm font-bold ${
                    modules.events.outstanding_balance > 0 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {formatCurrency(modules.events.outstanding_balance)}
                  </span>
                </div>
              </div>
            </div>
          </Link>
        )}
      </div>

      {/* Quick Actions & System Status */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
          <h2 className="text-xl font-medium text-[#2B3210] mb-4">Quick Actions</h2>
          <div className="space-y-2">
            {modules.procurement && (
              <Link
                to="/procurement"
                className="block px-4 py-3 rounded-lg bg-[#FBF8EF] hover:bg-[#E5E2D9] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ShoppingCart size={18} className="text-[#B2560D]" />
                  <p className="text-sm font-medium text-[#2B3210]">Create Purchase Request</p>
                </div>
              </Link>
            )}
            {modules.onboarding && (
              <Link
                to="/onboarding"
                className="block px-4 py-3 rounded-lg bg-[#FBF8EF] hover:bg-[#E5E2D9] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <UserPlus size={18} className="text-emerald-600" />
                  <p className="text-sm font-medium text-[#2B3210]">Onboard New Employee</p>
                </div>
              </Link>
            )}
            {modules.timesheets && (
              <Link
                to="/timesheets"
                className="block px-4 py-3 rounded-lg bg-[#FBF8EF] hover:bg-[#E5E2D9] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Clock size={18} className="text-blue-600" />
                  <p className="text-sm font-medium text-[#2B3210]">Submit Timesheet</p>
                </div>
              </Link>
            )}
            {modules.facilities && (
              <Link
                to="/facilities"
                className="block px-4 py-3 rounded-lg bg-[#FBF8EF] hover:bg-[#E5E2D9] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Wrench size={18} className="text-cyan-600" />
                  <p className="text-sm font-medium text-[#2B3210]">Create Work Order</p>
                </div>
              </Link>
            )}
            {modules.events && (
              <Link
                to="/events"
                className="block px-4 py-3 rounded-lg bg-[#FBF8EF] hover:bg-[#E5E2D9] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <CalendarCheck size={18} className="text-rose-600" />
                  <p className="text-sm font-medium text-[#2B3210]">Manage Events</p>
                </div>
              </Link>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
          <h2 className="text-xl font-medium text-[#2B3210] mb-4">System Status</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#505631]">All Systems</span>
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                Operational
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#505631]">Database</span>
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                Connected
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#505631]">API</span>
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                Running
              </span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-[#E5E2D9]">
              <span className="text-sm text-[#505631]">Your Role</span>
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#B2560D]/10 text-[#B2560D] capitalize">
                {overview?.user_role?.replace('_', ' ') || 'User'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
